/**
 * 🔒 Auto-Save Service para Formularios de Encuestas
 *
 * Sistema de respaldo local que guarda automáticamente datos críticos
 * en localStorage para evitar pérdidas por problemas de conexión.
 *
 * Casos de uso críticos:
 * - Entrevistas de admisión (firmas, información crítica)
 * - Formularios médicos (historial clínico)
 * - Documentos con archivos adjuntos
 * - Cualquier formulario con tiempo de completado >5 minutos
 */

const DRAFT_PREFIX = 'survey-draft';
const MAX_DRAFTS_PER_USER = 30; // Límite por alumno para evitar saturar localStorage
const DRAFTS_CHANGED_EVENT = 'survey-drafts-changed'; // Avisa a la UI (ej. menú de borradores) que cambió la lista
const AUTO_SAVE_INTERVAL = 10000; // 10 segundos por defecto
const DRAFT_EXPIRY_DAYS = 7; // Los drafts expiran después de 7 días
const MAX_FILE_SIZE_BASE64 = 500000; // 500KB - Archivos más grandes solo guardan metadata
const MAX_SIGNATURE_SIZE_BASE64 = 5000000; // 5MB - Firmas tienen prioridad máxima
const AUTO_RECOVER_TIME_THRESHOLD = 300000; // 5 minutos - Recuperación automática sin preguntar
const VERSION = '1.0';

/**
 * Genera una clave única para el draft
 */
const getDraftKey = (userId, surveyId, casoId = null) => {
  const base = `${DRAFT_PREFIX}-${userId}-${surveyId}`;
  return casoId ? `${base}-caso-${casoId}` : base;
};

const notifyDraftsChanged = () => {
  try {
    window.dispatchEvent(new Event(DRAFTS_CHANGED_EVENT));
  } catch (error) {
    // Entorno sin window: no hay UI que avisar
  }
};

/**
 * Convierte File a base64 (solo si es pequeño)
 * @param {File} file - Archivo a convertir
 * @param {Number} maxSize - Tamaño máximo permitido en bytes (por defecto 500KB)
 */
const fileToBase64 = (file, maxSize = MAX_FILE_SIZE_BASE64) => {
  return new Promise((resolve, reject) => {
    // Solo convertir archivos que cumplan con el límite de tamaño
    if (file.size > maxSize) {
      resolve(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Procesa archivos y los clasifica por tamaño
 * @param {Object} files - Objeto con archivos a procesar
 * @param {Boolean} isSignature - Si es true, usa límite mayor para firmas (prioridad máxima)
 */
const processFiles = async (files, isSignature = false) => {
  const filesSmall = {};
  const filesLarge = {};
  const maxSizeLimit = isSignature ? MAX_SIGNATURE_SIZE_BASE64 : MAX_FILE_SIZE_BASE64;
  const fileType = isSignature ? '✍️ Firma' : '📎 Archivo';

  for (const [questionId, fileData] of Object.entries(files)) {
    // Puede ser FileList, File, o array de Files
    const fileList = fileData instanceof FileList
      ? Array.from(fileData)
      : Array.isArray(fileData)
        ? fileData
        : [fileData];

    for (const file of fileList) {
      if (!(file instanceof File)) continue;

      const fileInfo = {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        timestamp: new Date().toISOString(),
        isSignature: isSignature
      };

      if (file.size <= maxSizeLimit) {
        // Archivo/firma pequeño - guardar completo en base64
        try {
          // ✅ Pasar maxSizeLimit correcto (5MB para firmas, 500KB para archivos)
          const dataURL = await fileToBase64(file, maxSizeLimit);
          if (dataURL) {
            filesSmall[questionId] = {
              ...fileInfo,
              dataURL,
              canRestore: true
            };
            const sizeKB = (file.size / 1024).toFixed(2);
            console.log(`💾 ${fileType} guardado: ${file.name} (${sizeKB} KB)`);
          } else {
            console.warn(`⚠️ No se pudo convertir ${fileType} a base64: ${file.name}`);
          }
        } catch (error) {
          console.error(`❌ Error convirtiendo ${fileType} ${file.name} a base64:`, error);
          // Si es firma y falla, intentar de todas formas guardarlo como metadata crítica
          if (isSignature) {
            filesLarge[questionId] = {
              ...fileInfo,
              mustReattach: true,
              critical: true, // Marcar como crítico
              warningShown: false
            };
          }
        }
      } else {
        // Archivo grande - solo guardar metadata
        filesLarge[questionId] = {
          ...fileInfo,
          mustReattach: true,
          critical: isSignature, // Firmas grandes son críticas
          warningShown: false
        };
        const sizeMB = (file.size / 1024 / 1024).toFixed(2);
        const priority = isSignature ? '🚨 CRÍTICO' : '';
        console.log(`⚠️ ${fileType} grande (solo metadata): ${file.name} (${sizeMB} MB) ${priority}`);
      }
    }
  }

  return { filesSmall, filesLarge };
};

/**
 * Calcula estadísticas de completitud del formulario
 */
const calculateCompletionStats = (responses, surveyMetadata) => {
  const totalFields = surveyMetadata?.totalQuestions || Object.keys(responses).length;
  const filledFields = Object.values(responses).filter(val => val && val !== '').length;
  const percentComplete = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;

  return {
    totalFields,
    filledFields,
    percentComplete,
    requiredFieldsFilled: 0, // TODO: Implementar lógica de campos requeridos
    requiredFieldsTotal: 0
  };
};

/**
 * Guarda un draft completo en localStorage
 */
export const saveDraft = async ({
  userId,
  surveyId,
  surveySlug,
  surveyName,
  surveyCategory,
  responses = {},
  signatures = {},
  signaturesUrls = {},
  files = {},
  customerData = {},
  interviewerData = {},
  emailSettings = {},
  apoderadoEmail = {},
  surveyMetadata = {},
  casoId = null
}) => {
  try {
    const draftKey = getDraftKey(userId, surveyId, casoId);
    const now = Date.now();

    // Procesar firmas (convertir a base64 con PRIORIDAD MÁXIMA - límite 5MB)
    console.log('🔒 Procesando firmas...', Object.keys(signatures).length);
    const { filesSmall: signaturesSmall, filesLarge: signaturesLarge } = await processFiles(signatures, true);

    // Procesar archivos normales (separar pequeños de grandes - límite 500KB)
    console.log('🔒 Procesando archivos...', Object.keys(files).length);
    const { filesSmall, filesLarge } = await processFiles(files, false);

    // Calcular estadísticas
    const completionStats = calculateCompletionStats(responses, surveyMetadata);

    const draft = {
      draftId: draftKey,
      version: VERSION,

      // Metadata del draft
      timestamp: now,
      lastSavedAt: new Date(now).toISOString(),
      expiresAt: new Date(now + (DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000)).toISOString(),
      saveCount: 1,

      // Contexto
      userId,
      surveyId,
      surveySlug,
      surveyName,
      surveyCategory,
      casoId,

      // Datos del usuario/entrevistador
      customerData,
      interviewerData,

      // Configuración
      emailSettings,
      apoderadoEmail,

      // Contenido del formulario
      responses,
      signaturesSmall,     // Firmas procesadas en base64
      signaturesLarge,     // Firmas grandes (solo metadata)
      signaturesUrls,      // URLs pre-subidas de firmas
      filesSmall,          // Archivos pequeños en base64
      filesLarge,          // Archivos grandes (solo metadata)

      // Metadata del survey
      surveyMetadata,

      // Estado de completitud
      completionStats
    };

    // Intentar guardar en localStorage
    try {
      localStorage.setItem(draftKey, JSON.stringify(draft));
    } catch (quotaError) {
      // Si falla por espacio, intentar limpiar y guardar versión reducida
      if (quotaError.name === 'QuotaExceededError') {
        console.warn('⚠️ QuotaExceededError - Intentando limpiar espacio...');
        cleanupAllExpiredDrafts();
        cleanupOldDrafts(userId, true);

        // Versión reducida sin archivos pequeños ni firmas
        const reducedDraft = {
          ...draft,
          signaturesSmall: {},
          filesSmall: {},
          warning: 'files_removed_due_to_quota'
        };

        localStorage.setItem(draftKey, JSON.stringify(reducedDraft));
        console.warn('⚠️ Draft guardado sin archivos/firmas pequeños por espacio limitado');
        notifyDraftsChanged();
        return { success: true, key: draftKey, draft: reducedDraft, warning: 'files_removed' };
      }
      throw quotaError;
    }

    // Limpiar drafts antiguos del usuario
    cleanupOldDrafts(userId);
    notifyDraftsChanged();

    console.log('💾 Draft guardado exitosamente:', {
      key: draftKey,
      surveyName,
      timestamp: draft.lastSavedAt,
      responsesCount: Object.keys(responses).length,
      signaturesSmallCount: Object.keys(signaturesSmall).length,
      signaturesLargeCount: Object.keys(signaturesLarge).length,
      signaturesUrlsCount: Object.keys(signaturesUrls).length,
      filesSmallCount: Object.keys(filesSmall).length,
      filesLargeCount: Object.keys(filesLarge).length,
      completion: `${completionStats.percentComplete}%`
    });

    return { success: true, key: draftKey, draft };

  } catch (error) {
    console.error('❌ Error guardando draft:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtiene un draft específico
 */
export const getDraft = (userId, surveyId, casoId = null) => {
  try {
    const draftKey = getDraftKey(userId, surveyId, casoId);
    const draftData = localStorage.getItem(draftKey);

    if (!draftData) {
      return null;
    }

    const draft = JSON.parse(draftData);

    // Verificar si el draft ha expirado
    if (draft.expiresAt && new Date(draft.expiresAt) < new Date()) {
      console.log('🗑️ Draft expirado, eliminando:', draftKey);
      localStorage.removeItem(draftKey);
      return null;
    }

    console.log('📂 Draft recuperado:', {
      key: draftKey,
      surveyName: draft.surveyName,
      lastSaved: draft.lastSavedAt,
      completion: `${draft.completionStats?.percentComplete || 0}%`,
      responsesCount: Object.keys(draft.responses || {}).length,
      signaturesSmallCount: Object.keys(draft.signaturesSmall || {}).length,
      signaturesLargeCount: Object.keys(draft.signaturesLarge || {}).length,
      filesSmallCount: Object.keys(draft.filesSmall || {}).length,
      filesLargeCount: Object.keys(draft.filesLarge || {}).length
    });

    return { key: draftKey, ...draft };

  } catch (error) {
    console.error('❌ Error obteniendo draft:', error);
    return null;
  }
};

/**
 * Actualiza el contador de guardados de un draft existente
 */
export const incrementSaveCount = (draftKey) => {
  try {
    const draftData = localStorage.getItem(draftKey);
    if (!draftData) return;

    const draft = JSON.parse(draftData);
    draft.saveCount = (draft.saveCount || 0) + 1;
    draft.lastSavedAt = new Date().toISOString();

    localStorage.setItem(draftKey, JSON.stringify(draft));
  } catch (error) {
    console.error('Error incrementando saveCount:', error);
  }
};

/**
 * Elimina un draft específico
 */
export const deleteDraft = (userId, surveyId, casoId = null) => {
  try {
    const draftKey = getDraftKey(userId, surveyId, casoId);
    localStorage.removeItem(draftKey);
    notifyDraftsChanged();

    console.log('🗑️ Draft eliminado:', draftKey);
    return { success: true, key: draftKey };
  } catch (error) {
    console.error('❌ Error eliminando draft:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Lee todos los drafts guardados (de todos los alumnos), más reciente primero.
 */
const readAllDrafts = () => {
  const drafts = [];
  Object.keys(localStorage)
    .filter(key => key.startsWith(`${DRAFT_PREFIX}-`))
    .forEach(key => {
      try {
        const draft = JSON.parse(localStorage.getItem(key));
        if (draft) drafts.push({ key, ...draft });
      } catch (error) {
        console.error(`Error parseando draft ${key}:`, error);
      }
    });
  return drafts.sort((a, b) => b.timestamp - a.timestamp);
};

/**
 * Obtiene todos los drafts de un alumno.
 * Se compara por el userId guardado dentro del draft y no por prefijo de clave:
 * un RUT con guion ("12345678-9") hacía que el prefijo de otro alumno ("12345678-")
 * coincidiera también con sus claves.
 */
export const getUserDrafts = (userId) => {
  try {
    return readAllDrafts().filter(draft => String(draft.userId) === String(userId));
  } catch (error) {
    console.error('❌ Error obteniendo drafts del usuario:', error);
    return [];
  }
};

/**
 * Resumen liviano de todos los borradores pendientes (todos los alumnos), para
 * listarlos en la UI sin exponer el contenido completo.
 */
export const listAllDrafts = () => {
  try {
    const now = new Date();
    return readAllDrafts()
      .filter(draft => !draft.expiresAt || new Date(draft.expiresAt) >= now)
      .map(draft => ({
        key: draft.key,
        userId: draft.userId,
        surveyId: draft.surveyId,
        surveyName: draft.surveyName,
        surveyCategory: draft.surveyCategory,
        casoId: draft.casoId ?? null,
        customerData: draft.customerData || {},
        lastSavedAt: draft.lastSavedAt,
        timestamp: draft.timestamp,
        expiresAt: draft.expiresAt,
        percentComplete: draft.completionStats?.percentComplete || 0,
      }));
  } catch (error) {
    console.error('❌ Error listando drafts:', error);
    return [];
  }
};

/**
 * Elimina un draft a partir de su clave de localStorage.
 */
export const deleteDraftByKey = (draftKey) => {
  try {
    if (!draftKey || !draftKey.startsWith(`${DRAFT_PREFIX}-`)) return { success: false };
    localStorage.removeItem(draftKey);
    notifyDraftsChanged();
    return { success: true, key: draftKey };
  } catch (error) {
    console.error('❌ Error eliminando draft:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Limpia drafts antiguos del usuario (mantiene solo los más recientes)
 */
export const cleanupOldDrafts = (userId, aggressive = false) => {
  try {
    const userDrafts = getUserDrafts(userId);
    const limit = aggressive ? Math.floor(MAX_DRAFTS_PER_USER / 2) : MAX_DRAFTS_PER_USER;

    if (userDrafts.length > limit) {
      const draftsToDelete = userDrafts.slice(limit);

      draftsToDelete.forEach(draft => {
        localStorage.removeItem(draft.key);
        console.log('🗑️ Draft antiguo eliminado:', draft.key);
      });

      console.log(`🧹 Limpieza completada: ${draftsToDelete.length} drafts eliminados`);
      notifyDraftsChanged();
    }
  } catch (error) {
    console.error('❌ Error limpiando drafts antiguos:', error);
  }
};

/**
 * Limpia todos los drafts expirados (global)
 */
export const cleanupAllExpiredDrafts = () => {
  try {
    const allKeys = Object.keys(localStorage);
    const draftKeys = allKeys.filter(key => key.startsWith(DRAFT_PREFIX));
    const now = new Date();
    let cleanedCount = 0;

    draftKeys.forEach(key => {
      try {
        const draft = JSON.parse(localStorage.getItem(key));
        if (draft.expiresAt && new Date(draft.expiresAt) < now) {
          localStorage.removeItem(key);
          cleanedCount++;
        }
      } catch (error) {
        // Si hay error parseando, eliminar el draft corrupto
        localStorage.removeItem(key);
        cleanedCount++;
      }
    });

    if (cleanedCount > 0) {
      console.log(`🧹 Limpieza global: ${cleanedCount} drafts expirados eliminados`);
    }
  } catch (error) {
    console.error('❌ Error limpiando drafts expirados:', error);
  }
};

/**
 * Obtiene estadísticas de uso de localStorage
 */
export const getStorageStats = () => {
  try {
    const allKeys = Object.keys(localStorage);
    const draftKeys = allKeys.filter(key => key.startsWith(DRAFT_PREFIX));

    let totalSize = 0;
    draftKeys.forEach(key => {
      const value = localStorage.getItem(key);
      totalSize += (key.length + value.length) * 2; // UTF-16 = 2 bytes por char
    });

    const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
    const estimatedQuota = 5; // ~5MB típico para localStorage
    const usagePercent = ((totalSize / (estimatedQuota * 1024 * 1024)) * 100).toFixed(1);

    return {
      draftsCount: draftKeys.length,
      totalSize,
      totalSizeMB,
      usagePercent,
      estimatedQuota
    };
  } catch (error) {
    console.error('❌ Error obteniendo estadísticas de storage:', error);
    return null;
  }
};

/**
 * Verifica si hay espacio disponible en localStorage
 */
export const hasStorageSpace = () => {
  try {
    const stats = getStorageStats();
    return stats && parseFloat(stats.usagePercent) < 80; // <80% de uso
  } catch (error) {
    return true; // Asumir que hay espacio si hay error
  }
};

/**
 * Exporta un draft como JSON descargable (backup manual)
 */
export const exportDraft = (userId, surveyId, casoId = null) => {
  try {
    const draft = getDraft(userId, surveyId, casoId);

    if (!draft) {
      return { success: false, error: 'Draft no encontrado' };
    }

    const dataStr = JSON.stringify(draft, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `draft-${draft.surveyName}-${draft.lastSavedAt}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();

    console.log('📥 Draft exportado:', exportFileDefaultName);
    return { success: true, fileName: exportFileDefaultName };
  } catch (error) {
    console.error('❌ Error exportando draft:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Importa un draft desde un archivo JSON
 */
export const importDraft = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const draft = JSON.parse(e.target.result);

        // Validar estructura básica
        if (!draft.userId || !draft.surveyId) {
          reject(new Error('Archivo de draft inválido'));
          return;
        }

        // Guardar draft importado
        const draftKey = getDraftKey(draft.userId, draft.surveyId, draft.casoId);
        localStorage.setItem(draftKey, JSON.stringify(draft));

        console.log('📤 Draft importado:', draftKey);
        resolve({ success: true, key: draftKey, draft });
      } catch (error) {
        console.error('❌ Error parseando archivo de importación:', error);
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Error leyendo archivo'));
    };

    reader.readAsText(file);
  });
};

/**
 * Restaura archivos pequeños desde base64 a FileList
 */
export const restoreFilesFromDraft = (filesSmall) => {
  const restoredFiles = {};

  for (const [questionId, fileData] of Object.entries(filesSmall)) {
    if (!fileData.canRestore || !fileData.dataURL) continue;

    try {
      // Convertir dataURL a Blob
      const byteString = atob(fileData.dataURL.split(',')[1]);
      const mimeString = fileData.dataURL.split(',')[0].split(':')[1].split(';')[0];

      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      const blob = new Blob([ab], { type: mimeString });

      // Crear File desde Blob
      const file = new File([blob], fileData.fileName, { type: fileData.fileType });

      // Crear FileList usando DataTransfer
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);

      restoredFiles[questionId] = dataTransfer.files;

      console.log(`✅ Archivo restaurado: ${fileData.fileName}`);
    } catch (error) {
      console.error(`❌ Error restaurando archivo ${fileData.fileName}:`, error);
    }
  }

  return restoredFiles;
};

// Ejecutar limpieza de drafts expirados al cargar el módulo
cleanupAllExpiredDrafts();

// Exportar constantes individualmente (las funciones ya están exportadas arriba con export const)
export {
  AUTO_SAVE_INTERVAL,
  MAX_FILE_SIZE_BASE64,
  MAX_SIGNATURE_SIZE_BASE64,
  AUTO_RECOVER_TIME_THRESHOLD,
  DRAFT_EXPIRY_DAYS,
  DRAFTS_CHANGED_EVENT
};

// Default export para compatibilidad futura
export default {
  saveDraft,
  getDraft,
  deleteDraft,
  getUserDrafts,
  listAllDrafts,
  deleteDraftByKey,
  cleanupOldDrafts,
  cleanupAllExpiredDrafts,
  getStorageStats,
  hasStorageSpace,
  exportDraft,
  importDraft,
  restoreFilesFromDraft,
  incrementSaveCount,

  // Constantes exportadas
  AUTO_SAVE_INTERVAL,
  MAX_FILE_SIZE_BASE64,
  MAX_SIGNATURE_SIZE_BASE64,
  AUTO_RECOVER_TIME_THRESHOLD,
  DRAFT_EXPIRY_DAYS
};
